import React, { useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { java } from '@codemirror/lang-java';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { json } from '@codemirror/lang-json';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import '../../Styles/CodeMirrorEditor.css';

// Custom theme inspired by the app's code styling
const modernDarkHighlight = HighlightStyle.define([
  { tag: t.comment, color: '#565f89', fontStyle: 'italic' },
  { tag: t.lineComment, color: '#565f89', fontStyle: 'italic' },
  { tag: t.blockComment, color: '#565f89', fontStyle: 'italic' },
  { tag: t.keyword, color: '#bb9af7' },
  { tag: [t.name, t.deleted, t.character, t.propertyName, t.macroName], color: '#c0caf5' },
  { tag: [t.function(t.variableName), t.labelName], color: '#7dcfff' },
  { tag: [t.color, t.constant(t.name), t.standard(t.name)], color: '#f7768e' },
  { tag: [t.definition(t.name), t.separator], color: '#c0caf5' },
  { tag: [t.typeName, t.className, t.number, t.changed, t.annotation, t.modifier, t.self, t.namespace], color: '#ff9e64' },
  { tag: [t.operator, t.operatorKeyword, t.url, t.escape, t.regexp, t.link, t.special(t.string)], color: '#7aa2f7' },
  { tag: [t.meta, t.comment], color: '#565f89' },
  { tag: t.strong, fontWeight: 'bold' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.link, color: '#7aa2f7', textDecoration: 'underline' },
  { tag: t.heading, fontWeight: 'bold', color: '#bb9af7' },
  { tag: [t.atom, t.bool, t.special(t.variableName)], color: '#ff9e64' },
  { tag: [t.processingInstruction, t.string, t.inserted], color: '#9ece6a' },
  { tag: t.invalid, color: '#f7768e' },
]);

const modernLightHighlight = HighlightStyle.define([
  { tag: t.comment, color: '#9096b0', fontStyle: 'italic' },
  { tag: t.lineComment, color: '#9096b0', fontStyle: 'italic' },
  { tag: t.blockComment, color: '#9096b0', fontStyle: 'italic' },
  { tag: t.keyword, color: '#a750d5' },
  { tag: [t.name, t.deleted, t.character, t.propertyName, t.macroName], color: '#343b58' },
  { tag: [t.function(t.variableName), t.labelName], color: '#0550ae' },
  { tag: [t.color, t.constant(t.name), t.standard(t.name)], color: '#d73a49' },
  { tag: [t.definition(t.name), t.separator], color: '#343b58' },
  { tag: [t.typeName, t.className, t.number, t.changed, t.annotation, t.modifier, t.self, t.namespace], color: '#cf6100' },
  { tag: [t.operator, t.operatorKeyword, t.url, t.escape, t.regexp, t.link, t.special(t.string)], color: '#0969da' },
  { tag: [t.meta, t.comment], color: '#9096b0' },
  { tag: t.strong, fontWeight: 'bold' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.link, color: '#0969da', textDecoration: 'underline' },
  { tag: t.heading, fontWeight: 'bold', color: '#a750d5' },
  { tag: [t.atom, t.bool, t.special(t.variableName)], color: '#cf6100' },
  { tag: [t.processingInstruction, t.string, t.inserted], color: '#1f883d' },
  { tag: t.invalid, color: '#d73a49' },
]);

interface CodeMirrorEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: string;
  placeholder?: string;
  readOnly?: boolean;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
}

export const CodeMirrorEditor: React.FC<CodeMirrorEditorProps> = ({
  value,
  onChange,
  language,
  placeholder,
  readOnly = false,
  theme = 'auto',
  className
}) => {
  const extensions = useMemo(() => {
    const languageExtensions = [];
    
    switch (language.toLowerCase()) {
      case 'javascript':
      case 'jsx':
        languageExtensions.push(javascript({ jsx: true }));
        break;
      case 'typescript':
      case 'tsx':
        languageExtensions.push(javascript({ typescript: true, jsx: language.includes('x') }));
        break;
      case 'python':
        languageExtensions.push(python());
        break;
      case 'java':
        languageExtensions.push(java());
        break;
      case 'css':
        languageExtensions.push(css());
        break;
      case 'html':
        languageExtensions.push(html());
        break;
      case 'json':
        languageExtensions.push(json());
        break;
      case 'sql':
        languageExtensions.push(sql());
        break;
      default:
        // For plain text and other languages, use no specific extension
        break;
    }

    // Add word wrap and other editor enhancements
    languageExtensions.push(EditorView.lineWrapping);
    
    return languageExtensions;
  }, [language]);

  const themeExtensions = useMemo(() => {
    // Auto-detect theme from CSS custom properties or use explicit theme
    const isDark = theme === 'dark' || 
      (theme === 'auto' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    
    if (isDark) {
      return [oneDark, syntaxHighlighting(modernDarkHighlight)];
    } else {
      return [syntaxHighlighting(modernLightHighlight)];
    }
  }, [theme]);

  return (
    <CodeMirror
      value={value}
      onChange={onChange}
      extensions={[...extensions, ...themeExtensions]}
      placeholder={placeholder}
      editable={!readOnly}
      className={className}
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        dropCursor: false,
        allowMultipleSelections: false,
        indentOnInput: true,
        bracketMatching: true,
        closeBrackets: true,
        autocompletion: true,
        highlightSelectionMatches: false,
        searchKeymap: true,
      }}
    />
  );
};